import arrayMove from 'array-move';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { useDeepCompareEffect } from 'react-use';

import { useActions } from '@mssgme/hooks';
import DragIcon from '@mssgme/ui/icons/drag.svg';
import styles from './CollectionsListDraggable.scss';

const SortableItem = SortableElement(({ col, t, draggable }) => {
    const ProductsCountTextTail = t(
        col.publishedProductsCount === 1
            ? 'PageProductsMain.productsSingle'
            : col.publishedProductsCount > 1 && col.publishedProductsCount < 5
                ? 'PageProductsMain.products24'
                : 'PageProductsMain.productsMany'
    );

    return (
        <li className={styles.listItem}>
            <Link to={`/p/products/collections/${col.id}`}>
                <div
                    className={cn(styles.status, {
                        [styles.active]: col.published && col.publishedProductsCount > 0,
                    })}
                />
                <span className={styles.label}>{col.name}</span>
                <span className={styles.counter}>
                    {col.publishedProductsCount} {ProductsCountTextTail}
                </span>
            </Link>
            {draggable && (
                <div className={styles.dragHandle} title={t('common:labels.dragTip')} data-element="drag-handle">
                    <DragIcon width="6" />
                </div>
            )}
        </li>
    );
});

const SortableList = SortableContainer(({ collections, t, draggable }) => {
    return (
        <ul className={styles.list}>
            {collections.map((collection, index) => (
                <SortableItem
                    key={collection.id}
                    index={index}
                    col={collection}
                    t={t}
                    disabled={!draggable}
                    draggable={draggable}
                />
            ))}
        </ul>
    );
});

export default function CollectionsListDraggable({ collections, draggable, isFetching }) {
    const { t } = useTranslation('cards');
    const actions = useActions();
    const [items, setCollections] = useState([]);

    const onSortEnd = async ({ oldIndex, newIndex }) => {
        const col1Id = items[oldIndex].id;
        const col2Id = items[newIndex].id;

        setCollections((prevItems) => arrayMove(prevItems, oldIndex, newIndex));

        if (oldIndex !== newIndex) {
            await actions.api.collections.sort({ payload: { col1Id, col2Id } });
        }
    };

    useDeepCompareEffect(() => {
        setCollections(collections);
    }, [collections]);

    return (
        <div className={cn({ [styles.root]: true, [styles.fetching]: isFetching })}>
            <SortableList
                collections={items}
                axis="y"
                lockAxis="y"
                onSortEnd={onSortEnd}
                helperClass={styles.sortable}
                t={t}
                draggable={draggable}
                shouldCancelStart={(e) => {
                    if (e.target.dataset.element !== 'drag-handle') {
                        return true;
                    }
                }}
            />
        </div>
    );
}

CollectionsListDraggable.propTypes = {
    collections: PropTypes.array.isRequired,
    draggable: PropTypes.bool.isRequired,
    isFetching: PropTypes.bool.isRequired,
};

CollectionsListDraggable.defaultProps = {
    draggable: true,
};
